import { Box } from '@mui/material';
import styled from 'styled-components';
import { CustomBarChart, Typography, colors, IconGraphy, Avatar, styles } from '@leapeasy/ui-kit';
import { useMemo } from 'react';
import { ReactComponent as DashboardEmptyStateIcon } from 'assets/dashboard-empty-state.svg';
import {
  buildTrailingApplicationVolume,
  buildTrailingPoliciesIssued,
} from 'utils/activityOverviewSixMonths';
import { chartSeriesHasValues } from 'utils/chartSeriesHasValues';

export const ApplicationVolume6MonthsChart = ({
  data,
  onNavigateApplications,
  onNavigatePolicies,
}) => {
  const sixMonths = useMemo(
    () => buildTrailingApplicationVolume(data?.sixmonthApp),
    [data?.sixmonthApp],
  );

  const policyIssuedSixMonths = useMemo(
    () =>
      buildTrailingPoliciesIssued(data?.appStageCountsByMonth, 6, new Date(), data?.appChartData2),
    [data?.appStageCountsByMonth, data?.appChartData2],
  );

  const hasApplicationVolumeData = chartSeriesHasValues(sixMonths, 'Application');
  const hasPolicyIssuedData = chartSeriesHasValues(policyIssuedSixMonths, 'Approved');

  const maxApplicationVolume = useMemo(() => {
    const maxValue = Math.max(...sixMonths.map((item) => Number(item?.Application || 0)), 0);
    if (maxValue <= 0) return 1;
    const step = Math.max(1, Math.ceil(maxValue / 4));
    return step * 4;
  }, [sixMonths]);

  const yTicks = useMemo(
    () => [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxApplicationVolume * ratio)),
    [maxApplicationVolume],
  );

  const formatVolumeTick = (value) => {
    const numericValue = Number(value) || 0;
    if (numericValue === 0) return '0';
    if (numericValue >= 1_000_000_000) {
      return `${numericValue / 1_000_000_000}B`;
    }
    if (numericValue >= 1_000_000) {
      return `${numericValue / 1_000_000}M`;
    }
    if (numericValue >= 1000) {
      return numericValue % 1000 === 0
        ? `${numericValue / 1000}k`
        : `${(numericValue / 1000).toFixed(1)}k`;
    }
    return String(Math.round(numericValue));
  };

  const maxPolicyIssuedVolume = useMemo(() => {
    const maxValue = Math.max(
      ...policyIssuedSixMonths.map((item) => Number(item?.Approved || 0)),
      0,
    );
    if (maxValue <= 0) return 1;
    const step = Math.max(1, Math.ceil(maxValue / 4));
    return step * 4;
  }, [policyIssuedSixMonths]);

  const policyTicks = useMemo(
    () => [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxPolicyIssuedVolume * ratio)),
    [maxPolicyIssuedVolume],
  );

  const renderEmptyState = () => (
    <ChartEmptyBody>
      <DashboardEmptyStateIcon />
      <Typography
        variant="body2"
        style={{
          textAlign: 'center',
          color: colors.neutral[900],
          maxWidth: '360px',
          fontWeight: '500',
        }}
      >
        No data for this period
      </Typography>
    </ChartEmptyBody>
  );

  return (
    <Box
      sx={{
        boxShadow: styles.boxShadow.sm,
        borderRadius: `${styles.borderRadius.large}px`,
        border: `1px solid ${colors.black[300]}`,
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
      }}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="Chart.SsidChart" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Activity Overview
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Overview of applications and policies issued volume over the past 6 months.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: '#f9f8f9',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1.8 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <Box display="flex" gap="4px" flexDirection="column">
            <Typography variant="body1" style={{ fontWeight: '600', color: colors.purple[900] }}>
              Applications Volume 6 months
            </Typography>
            <Typography variant="body3" style={{ color: colors.neutral[900] }}>
              Monthly application volumes over the past 6 months.
            </Typography>
          </Box>

          {hasApplicationVolumeData ? (
            <CustomBarChart
              chartWidth="100%"
              chartHeight={200}
              data={sixMonths}
              colors={{ Application: colors.purpleIrish[100] }}
              barDataKey="Application"
              barChartProps={{
                margin: { top: 4, right: 8, left: 0, bottom: 0 },
                barCategoryGap: 36,
              }}
              cartesianGridProps={{ stroke: colors.purple[100] }}
              xAxisProps={{
                axisLine: false,
                interval: 0,
                tick: { fill: '#A39AA9', fontSize: 10, fontWeight: 500 },
              }}
              yAxisProps={{
                domain: [0, maxApplicationVolume],
                ticks: yTicks,
                tickFormatter: formatVolumeTick,
                tick: { fill: '#6A5E71', fontSize: 10, fontWeight: 500 },
                width: 36,
              }}
              barProps={{
                radius: [0, 0, 0, 0],
                barSize: 21,
              }}
              showTooltip={false}
            />
          ) : (
            renderEmptyState()
          )}
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap="20px"
        >
          <Box display="flex" gap="4px" flexDirection="column">
            <Typography variant="body1" style={{ fontWeight: '600', color: colors.purple[900] }}>
              Policies Issued last 6 months
            </Typography>
            <Typography variant="body3" style={{ color: colors.neutral[900] }}>
              Monthly policies issued over the past 6 months.
            </Typography>
          </Box>

          {hasPolicyIssuedData ? (
            <CustomBarChart
              chartWidth="100%"
              chartHeight={200}
              data={policyIssuedSixMonths}
              colors={{
                Approved: colors.tomato[100],
              }}
              barDataKey="Approved"
              barChartProps={{
                margin: { top: 4, right: 8, left: 0, bottom: 0 },
                barCategoryGap: 36,
              }}
              cartesianGridProps={{ stroke: colors.purple[100] }}
              xAxisProps={{
                axisLine: false,
                interval: 0,
                tick: { fill: '#A39AA9', fontSize: 10, fontWeight: 500 },
              }}
              yAxisProps={{
                domain: [0, maxPolicyIssuedVolume],
                ticks: policyTicks,
                tickFormatter: formatVolumeTick,
                tick: { fill: '#6A5E71', fontSize: 10, fontWeight: 500 },
                width: 36,
              }}
              barProps={{
                radius: [0, 0, 0, 0],
                barSize: 21,
              }}
              showTooltip={false}
            />
          ) : (
            renderEmptyState()
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ChartEmptyBody = styled(Box)`
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
`;
